import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@halo/web-lib';
import {
  AlertTriangle,
  ArrowDownUp,
  CheckCircle2,
  ChevronRight,
  Download,
  ExternalLink,
  Filter,
  HeartPulse,
  Pause,
  PlayCircle,
  RefreshCw,
  Settings,
  Sparkles,
  X,
  XCircle,
  Zap,
} from 'lucide-react';

/* ─── Types ───────────────────────────────────── */

interface SlaRow {
  readonly storeId: number;
  readonly platform: string;
  readonly lastSuccessAt: string | null;
  readonly lagSeconds: number | null;
  readonly healthy: boolean;
}

interface SlaResponse {
  readonly rows: SlaRow[];
}

interface HistoryPoint {
  readonly storeId: number;
  readonly platform: string;
  readonly day: string;
  readonly completed: number;
  readonly errored: number;
  readonly partial: number;
}

interface HistoryResponse {
  readonly days: number;
  readonly rows: HistoryPoint[];
}

interface StoreListItem {
  readonly id: number;
  readonly name: string;
  readonly currency: string;
  readonly subscription: string;
  readonly agencyName: string | null;
}

interface IngestionRun {
  readonly id: string;
  readonly storeId: number;
  readonly platform: string;
  readonly runType: string;
  readonly status: string;
  readonly startedAt: string | null;
  readonly completedAt: string | null;
  readonly windowStart: string | null;
  readonly windowEnd: string | null;
  readonly rowsIngested: number;
  readonly errorMessage?: string | null;
  readonly triggerSource?: string | null;
}

type HealthBucket = 'healthy' | 'degraded' | 'stale';
type SortMode = 'store' | 'worst-first' | 'platform';

/* ─── Constants & helpers ─────────────────────── */

const HOUR = 3600;
const DAY = 86400;
const DEFAULT_DEGRADED_HOURS = 6;
const DEFAULT_STALE_HOURS = 24;
const THRESHOLDS_KEY = 'halo-admin-sla-thresholds-v1';

interface Thresholds {
  readonly degradedHours: number;
  readonly staleHours: number;
}

function loadThresholds(): Thresholds {
  try {
    const raw = localStorage.getItem(THRESHOLDS_KEY);
    if (!raw) return { degradedHours: DEFAULT_DEGRADED_HOURS, staleHours: DEFAULT_STALE_HOURS };
    const parsed = JSON.parse(raw) as Partial<Thresholds>;
    return {
      degradedHours: clamp(parsed.degradedHours ?? DEFAULT_DEGRADED_HOURS, 1, 168),
      staleHours: clamp(parsed.staleHours ?? DEFAULT_STALE_HOURS, 2, 720),
    };
  } catch {
    return { degradedHours: DEFAULT_DEGRADED_HOURS, staleHours: DEFAULT_STALE_HOURS };
  }
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.min(Math.max(n, lo), hi);
}

function bucketFor(lagSeconds: number | null, t: Thresholds): HealthBucket {
  if (lagSeconds === null || lagSeconds > t.staleHours * HOUR) return 'stale';
  if (lagSeconds >= t.degradedHours * HOUR) return 'degraded';
  return 'healthy';
}

function humanizeLag(lagSeconds: number | null): string {
  if (lagSeconds === null) return '—';
  if (lagSeconds < 60) return `${Math.max(0, Math.floor(lagSeconds))}s ago`;
  if (lagSeconds < HOUR) return `${Math.floor(lagSeconds / 60)}m ago`;
  if (lagSeconds < DAY) return `${Math.floor(lagSeconds / HOUR)}h ago`;
  return `${Math.floor(lagSeconds / DAY)}d ago`;
}

function cellClasses(bucket: HealthBucket): string {
  switch (bucket) {
    case 'healthy':
      return 'bg-green-50 dark:bg-green-950/40 text-green-700 dark:text-green-300 hover:bg-green-100 dark:hover:bg-green-900/40';
    case 'degraded':
      return 'bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/40';
    case 'stale':
      return 'bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/40';
  }
}

function downloadCsv(name: string, rows: string[][]): void {
  const csv = rows.map((r) => r.map(escapeCsv).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function escapeCsv(v: string): string {
  if (v.includes(',') || v.includes('"') || v.includes('\n')) {
    return `"${v.replace(/"/g, '""')}"`;
  }
  return v;
}

/* ─── Sparkline (inline SVG) ──────────────────── */

interface SparklineProps {
  readonly values: readonly { readonly completed: number; readonly errored: number }[];
  readonly width?: number;
  readonly height?: number;
}

function Sparkline({ values, width = 56, height = 14 }: SparklineProps) {
  if (values.length === 0) return <span className="text-gray-300 dark:text-gray-700 text-[9px]">·</span>;
  const max = Math.max(1, ...values.map((v) => v.completed + v.errored));
  const barW = width / values.length;
  return (
    <svg width={width} height={height} className="inline-block align-middle">
      {values.map((v, i) => {
        const total = v.completed + v.errored;
        const h = total > 0 ? (total / max) * height : 1;
        const okRatio = total > 0 ? v.completed / total : 0;
        const okH = h * okRatio;
        const errH = h - okH;
        return (
          <g key={i} transform={`translate(${i * barW}, ${height - h})`}>
            <rect width={Math.max(1, barW - 1)} height={errH} fill="currentColor" className="text-red-400" />
            <rect y={errH} width={Math.max(1, barW - 1)} height={okH} fill="currentColor" className="text-green-500" />
          </g>
        );
      })}
    </svg>
  );
}

/* ─── Cell popover ────────────────────────────── */

interface CellMenuProps {
  readonly storeId: number;
  readonly platform: string;
  readonly onClose: () => void;
  readonly onOpenDrawer: () => void;
  readonly onAction: (kind: 'run' | 'backfill', storeId: number, platform: string) => Promise<void>;
}

function CellMenu({ storeId, platform, onClose, onOpenDrawer, onAction }: CellMenuProps) {
  const [busy, setBusy] = useState<'run' | 'backfill' | null>(null);

  const trigger = async (kind: 'run' | 'backfill') => {
    setBusy(kind);
    try {
      await onAction(kind, storeId, platform);
    } finally {
      setBusy(null);
      onClose();
    }
  };

  return (
    <div className="absolute z-30 right-0 mt-1 w-56 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow-lg overflow-hidden">
      <button
        onClick={onOpenDrawer}
        className="w-full flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200"
      >
        <ChevronRight className="w-3.5 h-3.5" />
        View recent runs
      </button>
      <Link
        to={`/failed-jobs?storeId=${storeId}&platform=${encodeURIComponent(platform)}`}
        className="w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200"
        onClick={onClose}
      >
        <ExternalLink className="w-3.5 h-3.5" />
        Failed jobs for cell
      </Link>
      <div className="border-t border-gray-100 dark:border-gray-800" />
      <button
        onClick={() => trigger('run')}
        disabled={busy !== null}
        className="w-full flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-gray-50 dark:hover:bg-gray-800 text-blue-700 dark:text-blue-300 disabled:opacity-50"
      >
        <Zap className="w-3.5 h-3.5" />
        {busy === 'run' ? 'Triggering…' : 'Run now'}
      </button>
      <button
        onClick={() => trigger('backfill')}
        disabled={busy !== null}
        className="w-full flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-gray-50 dark:hover:bg-gray-800 text-amber-700 dark:text-amber-300 disabled:opacity-50"
      >
        <PlayCircle className="w-3.5 h-3.5" />
        {busy === 'backfill' ? 'Triggering…' : 'Backfill last 7 days'}
      </button>
    </div>
  );
}

/* ─── Recent runs drawer ──────────────────────── */

interface RunsDrawerProps {
  readonly storeId: number;
  readonly platform: string;
  readonly storeName: string;
  readonly onClose: () => void;
}

function RunsDrawer({ storeId, platform, storeName, onClose }: RunsDrawerProps) {
  const [runs, setRuns] = useState<IngestionRun[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get<IngestionRun[]>(
          `/api/v1/ingestion/admin/runs?storeId=${storeId}&platform=${encodeURIComponent(platform)}&limit=30`,
        );
        if (!cancelled && res.success && res.data) setRuns(res.data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [storeId, platform]);

  return (
    <div className="fixed inset-0 z-40 flex">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="ml-auto relative w-full max-w-xl bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-800 flex flex-col h-full overflow-hidden">
        <div className="flex items-start justify-between p-4 border-b border-gray-200 dark:border-gray-800">
          <div>
            <div className="text-xs uppercase tracking-wide text-gray-400">Recent runs</div>
            <div className="text-base font-semibold text-gray-900 dark:text-gray-100">
              {storeName} <span className="text-gray-400 dark:text-gray-500">/ {platform}</span>
            </div>
            <div className="text-[11px] text-gray-400 mt-0.5">store {storeId}</div>
          </div>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {loading ? (
            <div className="text-xs text-gray-400">Loading…</div>
          ) : runs.length === 0 ? (
            <div className="text-xs text-gray-400">No runs recorded for this cell.</div>
          ) : (
            runs.map((r) => {
              const ok = r.status === 'completed';
              const partial = r.status === 'partial';
              const failed = r.status === 'errored';
              const durationMs =
                r.startedAt && r.completedAt
                  ? new Date(r.completedAt).getTime() - new Date(r.startedAt).getTime()
                  : null;
              return (
                <div
                  key={r.id}
                  className="border border-gray-200 dark:border-gray-800 rounded-lg p-3 text-xs"
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium ${
                        ok
                          ? 'bg-green-50 dark:bg-green-950/40 text-green-700 dark:text-green-300'
                          : partial
                            ? 'bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300'
                            : failed
                              ? 'bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-300'
                              : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                      }`}
                    >
                      {ok ? (
                        <CheckCircle2 className="w-3 h-3" />
                      ) : failed ? (
                        <XCircle className="w-3 h-3" />
                      ) : (
                        <AlertTriangle className="w-3 h-3" />
                      )}
                      {r.status}
                    </span>
                    <span className="text-gray-400">{r.runType}</span>
                  </div>
                  <div className="text-gray-700 dark:text-gray-300">
                    {r.startedAt ? new Date(r.startedAt).toLocaleString() : '—'}
                    {durationMs !== null && (
                      <span className="text-gray-400 ml-1.5">({Math.round(durationMs / 1000)}s)</span>
                    )}
                  </div>
                  <div className="text-gray-400 dark:text-gray-500 mt-0.5">
                    {r.windowStart && r.windowEnd ? `${r.windowStart} → ${r.windowEnd}` : '—'} · {r.rowsIngested} rows
                    {r.triggerSource ? ` · ${r.triggerSource}` : ''}
                  </div>
                  {r.errorMessage ? (
                    <div className="mt-1.5 text-[11px] text-red-700 dark:text-red-300 font-mono bg-red-50 dark:bg-red-950/40 p-1.5 rounded break-words">
                      {r.errorMessage}
                    </div>
                  ) : null}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Threshold settings ──────────────────────── */

interface ThresholdsModalProps {
  readonly initial: Thresholds;
  readonly onSave: (t: Thresholds) => void;
  readonly onClose: () => void;
}

function ThresholdsModal({ initial, onSave, onClose }: ThresholdsModalProps) {
  const [degraded, setDegraded] = useState(initial.degradedHours);
  const [stale, setStale] = useState(initial.staleHours);
  const valid = degraded > 0 && stale > degraded;

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center gap-2">
            <Settings className="w-4 h-4" />
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">SLA thresholds</span>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-4 space-y-3 text-sm">
          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
              Degraded threshold (hours of lag)
            </label>
            <input
              type="number"
              min={1}
              max={168}
              value={degraded}
              onChange={(e) => setDegraded(parseInt(e.target.value, 10) || 1)}
              className="w-full px-2.5 py-1.5 text-sm rounded-md border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100"
            />
            <p className="text-[11px] text-gray-400 mt-1">Cells with lag ≥ this turn amber.</p>
          </div>
          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
              Stale threshold (hours of lag)
            </label>
            <input
              type="number"
              min={2}
              max={720}
              value={stale}
              onChange={(e) => setStale(parseInt(e.target.value, 10) || 2)}
              className="w-full px-2.5 py-1.5 text-sm rounded-md border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100"
            />
            <p className="text-[11px] text-gray-400 mt-1">Cells with lag &gt; this (or no data) turn red.</p>
          </div>
          {!valid && (
            <div className="text-[11px] text-red-600">Stale must be greater than degraded.</div>
          )}
        </div>
        <div className="flex justify-end gap-2 p-4 border-t border-gray-200 dark:border-gray-800">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-xs rounded-md border border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-950"
          >
            Cancel
          </button>
          <button
            onClick={() => onSave({ degradedHours: degraded, staleHours: stale })}
            disabled={!valid}
            className="px-3 py-1.5 text-xs rounded-md bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 hover:bg-gray-700 disabled:opacity-50"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Main page ───────────────────────────────── */

export function SlaPage() {
  const [rows, setRows] = useState<SlaRow[]>([]);
  const [history, setHistory] = useState<HistoryPoint[]>([]);
  const [stores, setStores] = useState<Map<number, StoreListItem>>(new Map());
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(Date.now());
  const [thresholds, setThresholds] = useState<Thresholds>(loadThresholds);
  const [thresholdsOpen, setThresholdsOpen] = useState(false);

  const [search, setSearch] = useState('');
  const [bucketFilter, setBucketFilter] = useState<HealthBucket | 'all'>('all');
  const [staleOnly, setStaleOnly] = useState(false);
  const [sortMode, setSortMode] = useState<SortMode>('store');

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState<'run' | 'backfill' | null>(null);
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const [drawer, setDrawer] = useState<{ storeId: number; platform: string } | null>(null);
  const [toast, setToast] = useState<{ kind: 'ok' | 'err'; msg: string } | null>(null);

  /* ─── Data fetch ────────────────────────────── */

  const fetchAll = useCallback(async () => {
    try {
      const [slaRes, histRes, storesRes] = await Promise.all([
        api.get<SlaResponse>('/api/v1/ingestion/admin/sla/per-store'),
        api.get<HistoryResponse>('/api/v1/ingestion/admin/sla/history?days=7'),
        api.get<StoreListItem[]>('/api/v1/admin/stores'),
      ]);

      if (slaRes.success && slaRes.data) setRows(slaRes.data.rows ?? []);
      if (histRes.success && histRes.data) setHistory(histRes.data.rows ?? []);
      if (storesRes.success && storesRes.data) {
        const map = new Map<number, StoreListItem>();
        for (const s of storesRes.data) map.set(s.id, s);
        setStores(map);
      }
      setLastRefresh(Date.now());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const interval = setInterval(fetchAll, 60_000);
    return () => clearInterval(interval);
  }, [fetchAll]);

  const [refreshCountdown, setRefreshCountdown] = useState(60);
  useEffect(() => {
    const t = setInterval(() => {
      setRefreshCountdown(Math.max(0, 60 - Math.floor((Date.now() - lastRefresh) / 1000)));
    }, 1000);
    return () => clearInterval(t);
  }, [lastRefresh]);

  /* ─── Toast helper ──────────────────────────── */

  const showToast = (kind: 'ok' | 'err', msg: string) => {
    setToast({ kind, msg });
    setTimeout(() => setToast(null), 4000);
  };

  /* ─── Mutations ─────────────────────────────── */

  const triggerOne = useCallback(
    async (kind: 'run' | 'backfill', storeId: number, platform: string) => {
      try {
        const url =
          kind === 'run' ? '/api/v1/ingestion/admin/pull-now' : '/api/v1/ingestion/admin/backfill';
        const body =
          kind === 'run'
            ? { storeId, platform, triggerSource: 'admin' }
            : { storeId, platform, days: 7, triggerSource: 'admin' };
        const res = await api.post(url, body);
        if (res.success) {
          showToast(
            'ok',
            kind === 'run'
              ? `Triggered pull for store ${storeId} (${platform})`
              : `Started 7-day backfill for store ${storeId} (${platform})`,
          );
        } else {
          showToast('err', res.error ?? `${kind} failed`);
        }
      } catch (err) {
        showToast('err', err instanceof Error ? err.message : `${kind} failed`);
      }
    },
    [],
  );

  const triggerBulk = useCallback(
    async (kind: 'run' | 'backfill') => {
      const targets = Array.from(selected).map((key) => {
        const [storeIdStr, platform] = key.split('::');
        return { storeId: parseInt(storeIdStr!, 10), platform: platform! };
      });
      if (targets.length === 0) return;
      if (
        !confirm(
          `${kind === 'run' ? 'Run' : 'Backfill 7d'} for ${targets.length} cell${targets.length === 1 ? '' : 's'}?`,
        )
      )
        return;

      setBulkBusy(kind);
      let ok = 0;
      let fail = 0;
      for (const t of targets) {
        try {
          await triggerOne(kind, t.storeId, t.platform);
          ok += 1;
        } catch {
          fail += 1;
        }
      }
      setBulkBusy(null);
      setSelected(new Set());
      showToast('ok', `${kind}: ${ok} succeeded, ${fail} failed`);
    },
    [selected, triggerOne],
  );

  /* ─── Derived ───────────────────────────────── */

  const { storeIds, platforms, grid, historyMap, tally, columnTally } = useMemo(() => {
    const storeSet = new Set<number>();
    const platformSet = new Set<string>();
    const cellMap = new Map<string, SlaRow>();
    for (const row of rows) {
      storeSet.add(row.storeId);
      platformSet.add(row.platform);
      cellMap.set(`${row.storeId}::${row.platform}`, row);
    }

    const histMap = new Map<string, HistoryPoint[]>();
    for (const h of history) {
      const k = `${h.storeId}::${h.platform}`;
      const arr = histMap.get(k) ?? [];
      arr.push(h);
      histMap.set(k, arr);
    }
    // Sort each cell's history ascending by day
    for (const [k, arr] of histMap.entries()) {
      arr.sort((a, b) => a.day.localeCompare(b.day));
      histMap.set(k, arr);
    }

    const sortedStores = Array.from(storeSet);
    const sortedPlatforms = Array.from(platformSet).sort((a, b) => a.localeCompare(b));

    let healthy = 0;
    let degraded = 0;
    let stale = 0;
    const colTally = new Map<string, { healthy: number; degraded: number; stale: number }>();
    for (const p of sortedPlatforms) colTally.set(p, { healthy: 0, degraded: 0, stale: 0 });

    for (const row of rows) {
      const b = bucketFor(row.lagSeconds, thresholds);
      const ct = colTally.get(row.platform);
      if (ct) ct[b] += 1;
      if (b === 'stale') stale += 1;
      else if (b === 'degraded') degraded += 1;
      else healthy += 1;
    }

    return {
      storeIds: sortedStores,
      platforms: sortedPlatforms,
      grid: cellMap,
      historyMap: histMap,
      tally: { healthy, degraded, stale },
      columnTally: colTally,
    };
  }, [rows, history, thresholds]);

  const visibleStores = useMemo(() => {
    const term = search.trim().toLowerCase();
    const matchesSearch = (id: number): boolean => {
      if (!term) return true;
      const s = stores.get(id);
      if (!s) return String(id).includes(term);
      return (
        String(id).includes(term) ||
        s.name.toLowerCase().includes(term) ||
        (s.agencyName?.toLowerCase().includes(term) ?? false)
      );
    };

    const worstBucket = (storeId: number): HealthBucket => {
      let worst: HealthBucket = 'healthy';
      for (const p of platforms) {
        const cell = grid.get(`${storeId}::${p}`);
        if (!cell) continue;
        const b = bucketFor(cell.lagSeconds, thresholds);
        if (b === 'stale') return 'stale';
        if (b === 'degraded' && worst === 'healthy') worst = 'degraded';
      }
      return worst;
    };

    const filtered = storeIds.filter((id) => {
      if (!matchesSearch(id)) return false;
      const w = worstBucket(id);
      if (staleOnly && w === 'healthy') return false;
      if (bucketFilter !== 'all' && w !== bucketFilter) return false;
      return true;
    });

    if (sortMode === 'worst-first') {
      const rank: Record<HealthBucket, number> = { stale: 0, degraded: 1, healthy: 2 };
      filtered.sort((a, b) => rank[worstBucket(a)] - rank[worstBucket(b)] || a - b);
    } else if (sortMode === 'store') {
      filtered.sort((a, b) => {
        const sa = stores.get(a)?.name ?? String(a);
        const sb = stores.get(b)?.name ?? String(b);
        return sa.localeCompare(sb);
      });
    }
    return filtered;
  }, [storeIds, platforms, grid, stores, search, bucketFilter, staleOnly, sortMode, thresholds]);

  /* ─── Selection ─────────────────────────────── */

  const toggleCell = (storeId: number, platform: string) => {
    const key = `${storeId}::${platform}`;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleRow = (storeId: number) => {
    const keys = platforms
      .filter((p) => grid.has(`${storeId}::${p}`))
      .map((p) => `${storeId}::${p}`);
    setSelected((prev) => {
      const next = new Set(prev);
      const allSelected = keys.every((k) => next.has(k));
      for (const k of keys) {
        if (allSelected) next.delete(k);
        else next.add(k);
      }
      return next;
    });
  };

  /* ─── Export ────────────────────────────────── */

  const handleExportCsv = () => {
    const header = ['Store ID', 'Store Name', 'Agency', 'Platform', 'Last Success', 'Lag', 'Bucket'];
    const dataRows: string[][] = [];
    for (const sid of visibleStores) {
      const s = stores.get(sid);
      for (const p of platforms) {
        const c = grid.get(`${sid}::${p}`);
        if (!c) continue;
        const b = bucketFor(c.lagSeconds, thresholds);
        dataRows.push([
          String(sid),
          s?.name ?? '',
          s?.agencyName ?? '',
          p,
          c.lastSuccessAt ?? '',
          humanizeLag(c.lagSeconds),
          b,
        ]);
      }
    }
    downloadCsv(`halo-sla-${new Date().toISOString().slice(0, 10)}.csv`, [header, ...dataRows]);
  };

  /* ─── Render ────────────────────────────────── */

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-64" />
        <div className="h-64 bg-gray-100 dark:bg-gray-800 rounded-xl" />
      </div>
    );
  }

  const hasData = visibleStores.length > 0 && platforms.length > 0;
  const totalCells = rows.length;
  const selectedCount = selected.size;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <HeartPulse className="w-5 h-5 text-gray-700 dark:text-gray-300" />
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Data SLA</h2>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
              Per-store data freshness across platforms. Healthy &lt;{thresholds.degradedHours}h, degraded{' '}
              {thresholds.degradedHours}–{thresholds.staleHours}h, stale &gt;{thresholds.staleHours}h.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-gray-400 dark:text-gray-500 flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
            {refreshCountdown}s
          </span>
          <button
            onClick={() => setThresholdsOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-950"
          >
            <Settings className="w-3.5 h-3.5" />
            Thresholds
          </button>
          <button
            onClick={handleExportCsv}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-950"
          >
            <Download className="w-3.5 h-3.5" />
            CSV
          </button>
          <button
            onClick={fetchAll}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-950"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Refresh
          </button>
        </div>
      </div>

      {/* Tally + controls */}
      <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
        <button
          onClick={() => setBucketFilter('healthy')}
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg font-medium ${
            bucketFilter === 'healthy'
              ? 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-200 ring-1 ring-green-500'
              : 'bg-green-50 dark:bg-green-950/40 text-green-700 dark:text-green-300'
          }`}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
          {tally.healthy} healthy
        </button>
        <button
          onClick={() => setBucketFilter('degraded')}
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg font-medium ${
            bucketFilter === 'degraded'
              ? 'bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-200 ring-1 ring-amber-500'
              : 'bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300'
          }`}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
          {tally.degraded} degraded
        </button>
        <button
          onClick={() => setBucketFilter('stale')}
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg font-medium ${
            bucketFilter === 'stale'
              ? 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-200 ring-1 ring-red-500'
              : 'bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-300'
          }`}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
          {tally.stale} stale
        </button>
        {bucketFilter !== 'all' && (
          <button
            onClick={() => setBucketFilter('all')}
            className="text-[11px] text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 underline"
          >
            clear
          </button>
        )}
        <span className="ml-2 text-gray-400">·</span>
        <span className="text-gray-400 dark:text-gray-500">{totalCells} cells total</span>
      </div>

      {/* Filter bar */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Filter className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search store name, ID, agency"
            className="w-full pl-8 pr-2.5 py-1.5 text-xs rounded-md border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 placeholder-gray-400"
          />
        </div>
        <label className="inline-flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300 cursor-pointer">
          <input
            type="checkbox"
            checked={staleOnly}
            onChange={(e) => setStaleOnly(e.target.checked)}
            className="rounded border-gray-300 dark:border-gray-700"
          />
          Stale only
        </label>
        <button
          onClick={() =>
            setSortMode((m) => (m === 'store' ? 'worst-first' : m === 'worst-first' ? 'platform' : 'store'))
          }
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-md border border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-950"
        >
          <ArrowDownUp className="w-3 h-3" />
          Sort: {sortMode === 'store' ? 'Store' : sortMode === 'worst-first' ? 'Worst first' : 'Platform'}
        </button>
      </div>

      {/* Bulk action bar */}
      {selectedCount > 0 && (
        <div className="mb-3 flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800 text-xs">
          <Sparkles className="w-3.5 h-3.5 text-blue-700 dark:text-blue-300" />
          <span className="text-blue-900 dark:text-blue-200 font-medium">
            {selectedCount} cell{selectedCount === 1 ? '' : 's'} selected
          </span>
          <span className="ml-auto flex items-center gap-2">
            <button
              onClick={() => triggerBulk('run')}
              disabled={bulkBusy !== null}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-blue-600 hover:bg-blue-700 text-white font-medium disabled:opacity-50"
            >
              <Zap className="w-3 h-3" />
              {bulkBusy === 'run' ? 'Running…' : 'Run now'}
            </button>
            <button
              onClick={() => triggerBulk('backfill')}
              disabled={bulkBusy !== null}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-amber-600 hover:bg-amber-700 text-white font-medium disabled:opacity-50"
            >
              <PlayCircle className="w-3 h-3" />
              {bulkBusy === 'backfill' ? 'Backfilling…' : 'Backfill 7d'}
            </button>
            <button
              onClick={() => setSelected(new Set())}
              className="text-blue-700 dark:text-blue-300 hover:underline"
            >
              clear
            </button>
          </span>
        </div>
      )}

      {/* Grid */}
      {!hasData ? (
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-10 flex flex-col items-center justify-center text-center">
          <HeartPulse className="w-10 h-10 text-gray-400 mb-2" />
          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {storeIds.length === 0 ? 'No SLA data.' : 'No matches for the current filters.'}
          </div>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-950 text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  <th className="text-left px-3 py-2 font-medium sticky left-0 bg-gray-50 dark:bg-gray-950 w-8" />
                  <th className="text-left px-3 py-2 font-medium sticky left-8 bg-gray-50 dark:bg-gray-950 min-w-[180px]">
                    Store
                  </th>
                  {platforms.map((p) => (
                    <th key={p} className="text-left px-3 py-2 font-medium whitespace-nowrap">
                      {p}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleStores.map((storeId) => {
                  const store = stores.get(storeId);
                  const rowKeys = platforms
                    .filter((p) => grid.has(`${storeId}::${p}`))
                    .map((p) => `${storeId}::${p}`);
                  const rowAllSelected = rowKeys.length > 0 && rowKeys.every((k) => selected.has(k));
                  return (
                    <tr
                      key={storeId}
                      className="border-t border-gray-100 dark:border-gray-800 hover:bg-gray-50/50 dark:hover:bg-gray-950/50"
                    >
                      <td className="px-3 py-2.5 sticky left-0 bg-white dark:bg-gray-900">
                        <input
                          type="checkbox"
                          checked={rowAllSelected}
                          onChange={() => toggleRow(storeId)}
                          className="rounded border-gray-300 dark:border-gray-700"
                        />
                      </td>
                      <td className="px-3 py-2.5 sticky left-8 bg-white dark:bg-gray-900">
                        <Link
                          to={`/stores/${storeId}`}
                          className="text-xs font-medium text-gray-900 dark:text-gray-100 hover:text-blue-600 dark:hover:text-blue-400 block truncate max-w-[200px]"
                          title={store?.name}
                        >
                          {store?.name ?? `store ${storeId}`}
                        </Link>
                        <div className="text-[10px] text-gray-400 font-mono">
                          #{storeId}
                          {store?.agencyName ? <span className="ml-1">· {store.agencyName}</span> : null}
                        </div>
                      </td>
                      {platforms.map((platform) => {
                        const key = `${storeId}::${platform}`;
                        const cell = grid.get(key);
                        const hist = historyMap.get(key) ?? [];
                        if (!cell) {
                          return (
                            <td key={platform} className="px-3 py-2.5">
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-gray-50 dark:bg-gray-900 text-gray-400 dark:text-gray-600">
                                —
                              </span>
                            </td>
                          );
                        }
                        const bucket = bucketFor(cell.lagSeconds, thresholds);
                        const isSelected = selected.has(key);
                        return (
                          <td key={platform} className="px-3 py-2.5 relative">
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleCell(storeId, platform)}
                                className="rounded border-gray-300 dark:border-gray-700"
                                onClick={(e) => e.stopPropagation()}
                              />
                              <button
                                onClick={() => setMenuFor(menuFor === key ? null : key)}
                                className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium whitespace-nowrap transition-colors ${cellClasses(bucket)}`}
                                title={cell.lastSuccessAt ?? 'no successful run recorded'}
                              >
                                {humanizeLag(cell.lagSeconds)}
                              </button>
                              <Sparkline values={hist} />
                            </div>
                            {menuFor === key && (
                              <>
                                <div className="fixed inset-0 z-20" onClick={() => setMenuFor(null)} />
                                <CellMenu
                                  storeId={storeId}
                                  platform={platform}
                                  onClose={() => setMenuFor(null)}
                                  onOpenDrawer={() => {
                                    setDrawer({ storeId, platform });
                                    setMenuFor(null);
                                  }}
                                  onAction={triggerOne}
                                />
                              </>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-950/50 text-[10px] text-gray-500 dark:text-gray-400">
                  <td className="px-3 py-2" />
                  <td className="px-3 py-2 sticky left-8 bg-gray-50/50 dark:bg-gray-950/50 font-medium">
                    Platform totals
                  </td>
                  {platforms.map((p) => {
                    const t = columnTally.get(p);
                    if (!t) return <td key={p} />;
                    return (
                      <td key={p} className="px-3 py-2 whitespace-nowrap">
                        <span className="text-green-600 dark:text-green-400">{t.healthy}</span>
                        <span className="text-gray-300 mx-0.5">/</span>
                        <span className="text-amber-600 dark:text-amber-400">{t.degraded}</span>
                        <span className="text-gray-300 mx-0.5">/</span>
                        <span className="text-red-600 dark:text-red-400">{t.stale}</span>
                      </td>
                    );
                  })}
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Drawer */}
      {drawer && (
        <RunsDrawer
          storeId={drawer.storeId}
          platform={drawer.platform}
          storeName={stores.get(drawer.storeId)?.name ?? `store ${drawer.storeId}`}
          onClose={() => setDrawer(null)}
        />
      )}

      {/* Thresholds modal */}
      {thresholdsOpen && (
        <ThresholdsModal
          initial={thresholds}
          onSave={(t) => {
            setThresholds(t);
            try {
              localStorage.setItem(THRESHOLDS_KEY, JSON.stringify(t));
            } catch {
              /* ignore quota errors */
            }
            setThresholdsOpen(false);
          }}
          onClose={() => setThresholdsOpen(false)}
        />
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-4 right-4 z-50">
          <div
            className={`px-4 py-2.5 rounded-lg shadow-lg text-xs font-medium ${
              toast.kind === 'ok'
                ? 'bg-green-600 text-white'
                : 'bg-red-600 text-white'
            }`}
          >
            {toast.msg}
          </div>
        </div>
      )}

      {/* Avoid unused-import warning for Pause when scheduler controls aren't wired yet */}
      <Pause className="hidden" />
    </div>
  );
}
